// File: MedicaidChipApplication.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './zoning-permit-form.css';

function MedicaidChipApplication() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // ---- Core state ----
  const [form, setForm] = useState({
    // Programs (GTSCare = Medicaid, CoverKids = CHIP, MSP = help with Medicare costs)
    applyMedicaid: true,
    applyCoverKids: false,
    applyMSP: false,

    // Person 1 (Head of household) — Contact & address
    fullName: '',
    dob: '',
    sex: '',
    email: '',
    phonePrimary: '',
    phonePrimaryType: 'Mobile',
    phoneOther: '',
    phoneOtherType: 'Home',
    languageSpoken: '',
    lettersLanguage: 'English', // English | Spanish
    residentOfState: 'Yes',
    tempOutOfState: 'No',
    planReturnToState: 'Yes',
    planReturnDate: '',

    // Home / Mailing
    homeStreet: '',
    homeApt: '',
    homeCity: '',
    homeState: '',
    homeZip: '',
    homeCounty: '',
    homeless: 'No',
    safeAtHome: 'No',
    safeAtHomeId: '',
    mailingStreet: '',
    mailingApt: '',
    mailingCity: '',
    mailingState: '',
    mailingZip: '',
    mailingCounty: '',

    // Plan selection (if approved)
    mcoChoice: 'Wellpoint', // Wellpoint | BlueCare GTS | UnitedHealthcare Community Plan

    // Race & ethnicity (optional)
    ethnicity: '',
    raceWhite: false,
    raceBlack: false,
    raceAIAN: false,
    raceAsian: false,
    raceNHOPI: false,
    raceOther: false,
    raceOtherText: '',
    preferNoRace: false,

    // Tax household basics
    willFileTaxes: '',
    willFileJointly: '',
    spouseName: '',
    willClaimDependents: '',
    dependentsListed: '',
    anyDependentsOutsideHome: '',
    claimedBySomeoneElse: '',
    taxFilerName: '',
    relationToTaxFiler: '',

    // Household caregiving & pregnancy
    livesWithChildUnder18: '',
    whoIsChild: '',
    relationshipToChild: '',
    pregnantOrLast12Months: '',
    babiesExpected: '',
    stillPregnant: '',
    dueDate: '',
    pregnancyEndDate: '',
    otherPregnanciesInLast12Months: '',
    otherPregnanciesBabies: '',
    otherPregnanciesEndDate: '',
    retroactiveCoverage3mo: '',
    retroactiveDateOfService: '',

    // Citizenship/immigration & veteran
    usCitizen: '',
    naturalizedOrDerived: '',
    alienNumber: '',
    certificateNumber: '',
    eligibleImmigration: '',
    immigrationStatus: '',
    immigrationStatusDate: '',
    immDocType: '',
    immDocId: '',
    hadDifferentImmStatusBefore: '',
    livedInUSSince1996: '',
    veteranOrActiveDutySelfSpouseParent: '',

    // Special categories / care needs
    enrolledMedicareAB: '',
    emergencyHelpPayingER: '',
    formerFosterCareAt18: '',
    breastCervicalTreatmentUnder65: '',
    inMedicalFacility30Days: '',
    facilityName: '',
    facilityAdmitDate: '',
    doctorName: '',
    doctorPhone: '',
    livesInNursingHome: '',
    nursingHomeName: '',
    needsHospice: '',
    qualifiesNursingHomePrefersHome: '',
    icfIID: '',
    needsECFChoicesHCBS: '',
    wantsMedicareSavingsProgram: '',
    gotSSIInPast: '',
    ssiEndDate: '',
    blindDisabledWorkExpenses: '',
    under22WorkingFullTime: '',

    // Employment (Person 1) – job 1
    job1Employer: '',
    job1EmployerAddress: '',
    job1EmployerCity: '',
    job1EmployerState: '',
    job1EmployerZip: '',
    job1EmployerPhone: '',
    job1WagesPerPay: '',
    job1PayFrequency: '', // Hourly, Weekly, Every 2 weeks, Twice a month, Monthly, Yearly, etc.
    job1AvgHoursPerPay: '',

    // Employment (Person 1) – job 2
    job2Employer: '',
    job2EmployerAddress: '',
    job2EmployerCity: '',
    job2EmployerState: '',
    job2EmployerZip: '',
    job2EmployerPhone: '',
    job2WagesPerPay: '',
    job2PayFrequency: '',
    job2AvgHoursPerPay: '',

    // Other income (Person 1) this month
    oiNone: false,
    oiSocialSecurity: '',
    oiUnemployment: '',
    oiPension: '',
    oiCensusWorker: '',
    oiRetirementAccounts: '',
    oiNetFarmFish: '',
    oiRentalRoyalty: '',
    oiAlimonyReceived: '',
    oiLottery: '',
    oiOther: '',
    oiOtherType: '',
    // In-kind support (only if Social Security is checked in paper app)
    iksNeeded: 'No',
    iksHelperLivesWithYou: '',
    iksWhatTheyPayFor: '',
    iksExpenseTotal: '',
    iksYouPay: '',
    iksTheyPay: '',
    iksHouseholdCount: '',
    iksAllPublicAssistance: '',

    // Lump sum & self-employment
    lumpSumInLast12mo: '',
    lumpSumAmount: '',
    lumpSumDate: '',
    lumpSumSource: '',
    selfEmployed: '',
    selfEmploymentType: '',
    selfEmploymentKind: '',
    selfEmploymentNetMonthly: '',

    // Deductions (before tax)
    dtMedicalIns: '',
    dtDentalIns: '',
    dtVisionIns: '',
    dtFSA: '',
    dtDeferredComp: '',
    dtPreTaxLife: '',
    dtOther: '',
    dtOtherType: '',

    // Tax deductions
    tdAlimonyPaid: '',
    tdAlimonyOrderDate: '',
    tdHSA: '',
    tdStudentLoanInterest: '',
    tdTuitionFees: '',
    tdEducatorExpenses: '',
    tdBusinessExpenses: '',
    tdSelfEmploymentDeductible: '',
    tdMilitaryMoveExpenseTotal: '',
    tdOther: '',
    tdOtherType: '',

    // Current health coverage (non-GTSCare/CoverKids)
    hasOtherInsurance: '',
    otherInsCompany: '',
    otherInsType: '', // Medicare, TRICARE, Employer, COBRA, Retiree, VA, Peace Corps, Limited
    otherInsEmployerName: '',
    otherInsWhoIsCovered: '',
    otherInsPolicyNumber: '',
    otherInsGroupNumber: '',
    otherInsStartDate: '',
    otherInsStateEmployeePlan: '',
    otherInsLimitedPlan: '',
    otherInsMaternityCovered: '',
    lostMedicareDueToWork: '',
    lostMedicareWho: '',
    accessToOtherCoverageNotEnrolled: '',

    // Household-level resources (optional for pregnant/kids/caretakers)
    // Simple dynamic lists below

    // Expenses & recent medical bills (household)
    hasShelterOrUtilityOrChildSupport: '',
    hasMedicalDentalLast3mo: '',
    medBillAmount: '',
    medBillDateOfService: '',
    medBillPayTo: '',

    // Renewal permissions
    permitTaxDataYears: '5', // 1..5 or '0' (Don't use tax data)
    permitCreditAgencyCheck: 'Yes',

    // Declaration
    certify: false,
    signature: '',
    signedDate: '',
  });

  // Dynamic household members beyond Person 1 (Step 2: Person 2+)
  const [household, setHousehold] = useState([
    // Example blank person object for add-more flow
  ]);

  // Family resources / property / vehicles
  const [resourceRows, setResourceRows] = useState([{ type: '', value: '', owners: '' }]);
  const [propertyRows, setPropertyRows] = useState([{
    owners: '',
    use: '',
    anyoneLiveThere: '',
    whoLivesThere: '',
    intendReturn: '',
    spouseOrChildLivesThere: '',
    receivesRent: '',
    monthlyRentIncome: '',
    amountOwed: '',
    value: '',
    address: '',
    city: '', state: '', zip: '', county: '',
  }]);
  const [lifeInsRows, setLifeInsRows] = useState([{
    owners: '',
    policyType: '', // Term/Group | Whole/Universal
    faceValue: '',
    cashSurrender: '',
    policyNumber: '',
    company: '',
    companyAddress: '',
    companyCity: '', companyState: '', companyZip: '',
    companyPhone: '',
  }]);
  const [burialRows, setBurialRows] = useState([{ value: '', amountOwed: '', type: '', designatedFor: '' }]);
  const [vehicleRows, setVehicleRows] = useState([{ owners: '', year: '', make: '', model: '', amountOwed: '', value: '', incomeFromUse: '', use: '' }]);
  const [soldGiftedRows, setSoldGiftedRows] = useState([{ resourceType: '', whoOwned: '', toWhom: '', why: '', date: '', valueAtTime: '', moneyReceived: '' }]);

  // ---- Helpers ----
  const onlyDigits = (s) => (s || '').replace(/\D+/g, '');
  const isFiveZip = (s) => /^\d{5}$/.test(s);
  const isTenPhone = (s) => /^\d{10}$/.test(onlyDigits(s));
  const isTwoLetterState = (s) => /^[A-Za-z]{2}$/i.test(s);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === 'checkbox' ? checked : value;
    // Auto-uppercase for state fields
    if (name.toLowerCase().endsWith('state')) {
      setForm((f) => ({ ...f, [name]: v.toString().toUpperCase() }));
    } else {
      setForm((f) => ({ ...f, [name]: v }));
    }
    setErrors((ep) => ({ ...ep, [name]: undefined }));
  };

  const onChangeHH = (idx, key, value) => {
    const copy = [...household];
    copy[idx] = { ...(copy[idx] || {}), [key]: value };
    setHousehold(copy);
  };

  const addPerson = () =>
    setHousehold((rows) => [
      ...rows,
      {
        fullName: '',
        dob: '',
        sex: '',
        relationshipToP1: '',
        ssn: '',
        applying: 'Yes',
        ethnicity: '',
        // race flags
        raceWhite: false, raceBlack: false, raceAIAN: false, raceAsian: false, raceNHOPI: false, raceOther: false, raceOtherText: '', preferNoRace: false,
        // residency & school
        residentOfState: 'Yes',
        tempOutOfState: 'No',
        planReturnToState: 'Yes',
        planReturnDate: '',
        schoolEnrollment: '', // None/LT6h/6-7/8-11/12+
        // immigration & veteran
        usCitizen: '',
        naturalizedOrDerived: '',
        alienNumber: '', certificateNumber: '',
        eligibleImmigration: '',
        immigrationStatus: '', immigrationStatusDate: '', immDocType: '', immDocId: '', hadDifferentImmStatusBefore: '', livedInUSSince1996: '',
        veteranOrActiveDutySelfSpouseParent: '',
        // AI/AN
        aiAn: 'No',
        aiAnTribe: '',
        aiAnEverGotIHS: '',
        aiAnEligibleIHS: '',
        // Pregnancy/other categories
        pregnantOrLast12Months: '',
        babiesExpected: '',
        stillPregnant: '',
        dueDate: '',
        pregnancyEndDate: '',
        retroactiveCoverage3mo: '',
        retroactiveDateOfService: '',
        medicareAB: '',
        emergencyHelpPayingER: '',
        fosterCareAt18: '',
        breastCervicalTreatmentUnder65: '',
        inMedicalFacility30Days: '',
        facilityName: '',
        facilityAdmitDate: '',
        doctorName: '',
        doctorPhone: '',
        livesInNursingHome: '',
        nursingHomeName: '',
        needsHospice: '',
        qualifiesNursingHomePrefersHome: '',
        icfIID: '',
        needsECFChoicesHCBS: '',
        wantsMedicareSavingsProgram: '',
        ssiPast: '',
        ssiEndDate: '',
        blindDisabledWorkExpenses: '',
        under22WorkingFullTime: '',
        // Managed care plan preference
        mcoChoice: form.mcoChoice,
      },
    ]);

  const removePerson = (idx) => {
    const copy = [...household];
    copy.splice(idx, 1);
    setHousehold(copy);
  };

  const updateRow = (rows, setRows, idx, key, value) => {
    const copy = [...rows];
    copy[idx] = { ...copy[idx], [key]: value };
    setRows(copy);
  };
  const addRow = (rows, setRows, blank) => setRows([...rows, { ...blank }]);
  const removeRow = (rows, setRows, idx, blank) => {
    const copy = [...rows];
    copy.splice(idx, 1);
    setRows(copy.length ? copy : [{ ...blank }]);
  };

  // ---- Validation (keep demo-friendly) ----
  const validate = () => {
    const e = {};
    // Only require the first field (fullName) for demo purposes
    if (!String(form.fullName || '').trim()) {
      e.fullName = 'Required';
    }
    // Keep format validation for fields that are filled in (but don't require them)
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Invalid email';
    // Format validation only
    if (form.homeState && !isTwoLetterState(form.homeState)) e.homeState = 'Use 2-letter code (e.g., TN)';
    if (form.mailingState && form.mailingState.trim() && !isTwoLetterState(form.mailingState)) e.mailingState = 'Use 2-letter code (e.g., TN)';
    if (form.homeZip && !isFiveZip(form.homeZip)) e.homeZip = '5-digit ZIP';
    if (form.mailingZip && form.mailingZip.trim() && !isFiveZip(form.mailingZip)) e.mailingZip = '5-digit ZIP';
    if (form.phonePrimary && !isTenPhone(form.phonePrimary)) e.phonePrimary = '10 digits';
    if (form.phoneOther && form.phoneOther.trim() && !isTenPhone(form.phoneOther)) e.phoneOther = '10 digits';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true); // success screen
    }, 500);
  };

  const resetForm = () => window.location.reload();

  return (
    <section className="department-details-section">
      <div className="container gts-wide-container">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="department-details-content-box">
              <h4 className="department-details-title">Medicaid &amp; CoverKids (CHIP) Application</h4>
              <p className="gts-form-overview">
                Apply for free or low-cost health insurance (GTSCare/Medicaid, CoverKids/CHIP, or Medicare Savings Programs). This is a{' '}
                <strong>demo-only</strong> submission—no data is stored or transmitted.
              </p>
              <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                <a 
                  href="/guides/GTSCarePaperApp.pdf" 
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#e75934',
                    textDecoration: 'none',
                    border: '1px solid #e75934',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e75934';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e75934';
                  }}
                >
                  <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                  Download Application (PDF)
                </a>
                <Link 
                  to="/services/health-coverage"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    fontSize: '14px',
                    color: '#666',
                    textDecoration: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'transparent',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Health Coverage Info
                </Link>
              </div>
            </div>

            {/* Success screen */}
            {submitted ? (
              <div className="gts-preview success" role="status" aria-live="polite">
                <h5>🎉 Application submitted!</h5>
                <p>
                  Thank you. Your application has been recorded in this demo environment. A confirmation email would be sent in a live system.
                </p>
                <div className="gts-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                  <button 
                    onClick={() => window.print()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Print this page
                  </button>
                  <a 
                    href="/guides/GTSCarePaperApp.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <Link 
                    to="/forms"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#666',
                      textDecoration: 'none',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Back to Forms &amp; Applications
                  </Link>
                </div>
              </div>
            ) : (
              <form className="gts-form" onSubmit={onSubmit} noValidate>
                {/* Programs */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Programs</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="applyMedicaid" checked={form.applyMedicaid} onChange={onChange} />
                      <span>Apply for GTSCare (Medicaid)</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="applyCoverKids" checked={form.applyCoverKids} onChange={onChange} />
                      <span>Apply for CoverKids (CHIP)</span>
                    </label>
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="applyMSP" checked={form.applyMSP} onChange={onChange} />
                      <span>Help paying Medicare costs (QMB/SLMB)</span>
                    </label>
                    {errors.programs && <em className="gts-error">{errors.programs}</em>}
                  </div>
                </div>

                {/* Person 1 — Contact & Address */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Person 1 — Head of Household</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2">
                      <span>Full Name <span className="gts-required">*</span></span>
                      <input name="fullName" value={form.fullName} onChange={onChange} />
                      {errors.fullName && <em className="gts-error">{errors.fullName}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Date of Birth</span>
                      <input type="date" name="dob" value={form.dob} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>Sex</span>
                      <select name="sex" value={form.sex} onChange={onChange}>
                        <option value="">Select…</option>
                        <option>Male</option>
                        <option>Female</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Email <span className="gts-required">*</span></span>
                      <input type="email" name="email" value={form.email} onChange={onChange} />
                      {errors.email && <em className="gts-error">{errors.email}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Primary Phone</span>
                      <input name="phonePrimary" value={form.phonePrimary} onChange={onChange} maxLength={14} />
                      {errors.phonePrimary && <em className="gts-error">{errors.phonePrimary}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Type</span>
                      <select name="phonePrimaryType" value={form.phonePrimaryType} onChange={onChange}>
                        <option>Mobile</option>
                        <option>Home</option>
                        <option>Work</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Other Phone</span>
                      <input name="phoneOther" value={form.phoneOther} onChange={onChange} maxLength={14} />
                      {errors.phoneOther && <em className="gts-error">{errors.phoneOther}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Type</span>
                      <select name="phoneOtherType" value={form.phoneOtherType} onChange={onChange}>
                        <option>Mobile</option>
                        <option>Home</option>
                        <option>Work</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Language You Speak Most</span>
                      <input name="languageSpoken" value={form.languageSpoken} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>Letters Language</span>
                      <select name="lettersLanguage" value={form.lettersLanguage} onChange={onChange}>
                        <option>English</option>
                        <option>Spanish</option>
                      </select>
                    </label>

                    <fieldset className="gts-field">
                      <legend>Are you a resident of GTS?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="residentOfState" value={o} checked={form.residentOfState===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Temporarily living out of state?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="tempOutOfState" value={o} checked={form.tempOutOfState===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.tempOutOfState === 'Yes' && (
                      <>
                        <fieldset className="gts-field">
                          <legend>Do you plan to return to GTS?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label className="gts-radio" key={o}>
                                <input type="radio" name="planReturnToState" value={o} checked={form.planReturnToState===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <label className="gts-field">
                          <span>Date you plan to return</span>
                          <input type="date" name="planReturnDate" value={form.planReturnDate} onChange={onChange} />
                        </label>
                      </>
                    )}

                    <fieldset className="gts-field">
                      <legend>Are you homeless or living in a shelter?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="homeless" value={o} checked={form.homeless===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Are you in the Safe at Home Program?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="safeAtHome" value={o} checked={form.safeAtHome===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.safeAtHome === 'Yes' && (
                      <label className="gts-field">
                        <span>Safe at Home ID</span>
                        <input name="safeAtHomeId" value={form.safeAtHomeId} onChange={onChange} />
                      </label>
                    )}

                    <label className="gts-field gts-span-2">
                      <span>Home Street {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeStreet" value={form.homeStreet} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>Apt/Suite</span>
                      <input name="homeApt" value={form.homeApt} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>City {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeCity" value={form.homeCity} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>State (2) {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeState" value={form.homeState} onChange={onChange} maxLength={2} placeholder="TN" />
                      {errors.homeState && <em className="gts-error">{errors.homeState}</em>}
                    </label>
                    <label className="gts-field">
                      <span>ZIP (5) {form.homeless==='No' && <span className="gts-required">*</span>}</span>
                      <input name="homeZip" value={form.homeZip} onChange={onChange} maxLength={5} />
                      {errors.homeZip && <em className="gts-error">{errors.homeZip}</em>}
                    </label>
                    <label className="gts-field">
                      <span>County</span>
                      <input name="homeCounty" value={form.homeCounty} onChange={onChange} />
                    </label>
                    {errors.address && <em className="gts-error">{errors.address}</em>}

                    <label className="gts-field gts-span-2">
                      <span>Mailing Street (if different)</span>
                      <input name="mailingStreet" value={form.mailingStreet} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>Apt/Suite</span>
                      <input name="mailingApt" value={form.mailingApt} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>City</span>
                      <input name="mailingCity" value={form.mailingCity} onChange={onChange} />
                    </label>
                    <label className="gts-field">
                      <span>State (2)</span>
                      <input name="mailingState" value={form.mailingState} onChange={onChange} maxLength={2} />
                      {errors.mailingState && <em className="gts-error">{errors.mailingState}</em>}
                    </label>
                    <label className="gts-field">
                      <span>ZIP (5)</span>
                      <input name="mailingZip" value={form.mailingZip} onChange={onChange} maxLength={5} />
                      {errors.mailingZip && <em className="gts-error">{errors.mailingZip}</em>}
                    </label>
                    <label className="gts-field">
                      <span>County</span>
                      <input name="mailingCounty" value={form.mailingCounty} onChange={onChange} />
                    </label>
                  </div>
                </div>

                {/* Plan preference & demographics */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Plan Preference & Demographics</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Preferred Health Plan (if approved)</span>
                      <select name="mcoChoice" value={form.mcoChoice} onChange={onChange}>
                        <option>Wellpoint</option>
                        <option>BlueCare GTS</option>
                        <option>UnitedHealthcare Community Plan</option>
                      </select>
                    </label>

                    <label className="gts-field">
                      <span>Hispanic/Latino (optional)</span>
                      <input name="ethnicity" value={form.ethnicity} onChange={onChange} placeholder="e.g., Mexican, Puerto Rican, Cuban" />
                    </label>

                    <fieldset className="gts-field gts-span-2">
                      <legend>Race (optional — check all that apply)</legend>
                      <div className="gts-radio-list">
                        <label className="gts-radio"><input type="checkbox" name="raceWhite" checked={form.raceWhite} onChange={onChange} /><span>White</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceBlack" checked={form.raceBlack} onChange={onChange} /><span>Black or African American</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceAIAN" checked={form.raceAIAN} onChange={onChange} /><span>American Indian or Alaska Native</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceAsian" checked={form.raceAsian} onChange={onChange} /><span>Asian</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceNHOPI" checked={form.raceNHOPI} onChange={onChange} /><span>Native Hawaiian or Other Pacific Islander</span></label>
                        <label className="gts-radio"><input type="checkbox" name="raceOther" checked={form.raceOther} onChange={onChange} /><span>Other</span></label>
                        <label className="gts-radio" style={{ maxWidth: 360 }}>
                          <input type="checkbox" name="preferNoRace" checked={form.preferNoRace} onChange={onChange} />
                          <span>Prefer not to answer</span>
                        </label>
                      </div>
                      {form.raceOther && (
                        <div style={{ marginTop: 8 }}>
                          <input name="raceOtherText" value={form.raceOtherText} onChange={onChange} placeholder="Describe 'Other' race" />
                        </div>
                      )}
                    </fieldset>
                  </div>
                </div>

                {/* Citizenship/Immigration & Veteran */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Citizenship / Immigration &amp; Veteran Status</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>U.S. Citizen or U.S. National?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="usCitizen" value={o} checked={form.usCitizen===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Naturalized or Derived Citizen?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="naturalizedOrDerived" value={o} checked={form.naturalizedOrDerived===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.naturalizedOrDerived === 'Yes' && (
                      <>
                        <label className="gts-field">
                          <span>Alien Number</span>
                          <input name="alienNumber" value={form.alienNumber} onChange={onChange} />
                        </label>
                        <label className="gts-field">
                          <span>Certificate Number</span>
                          <input name="certificateNumber" value={form.certificateNumber} onChange={onChange} />
                        </label>
                      </>
                    )}

                    <fieldset className="gts-field gts-span-2">
                      <legend>Eligible Immigration Status</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="eligibleImmigration" value={o} checked={form.eligibleImmigration===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                      {form.eligibleImmigration === 'Yes' && (
                        <div className="gts-form-grid" style={{ marginTop: 8 }}>
                          <label className="gts-field">
                            <span>Status</span>
                            <input name="immigrationStatus" value={form.immigrationStatus} onChange={onChange} />
                          </label>
                          <label className="gts-field">
                            <span>Date Gained Status</span>
                            <input type="date" name="immigrationStatusDate" value={form.immigrationStatusDate} onChange={onChange} />
                          </label>
                          <label className="gts-field">
                            <span>Document Type</span>
                            <input name="immDocType" value={form.immDocType} onChange={onChange} placeholder="Alien #, I-94, Card #, Passport #, etc." />
                          </label>
                          <label className="gts-field">
                            <span>Document ID</span>
                            <input name="immDocId" value={form.immDocId} onChange={onChange} />
                          </label>
                          <label className="gts-field">
                            <span>Had a different immigration status before?</span>
                            <select name="hadDifferentImmStatusBefore" value={form.hadDifferentImmStatusBefore} onChange={onChange}>
                              <option value="">Select…</option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </label>
                          <label className="gts-field">
                            <span>Lived in the U.S. since 1996?</span>
                            <select name="livedInUSSince1996" value={form.livedInUSSince1996} onChange={onChange}>
                              <option value="">Select…</option>
                              <option>Yes</option>
                              <option>No</option>
                            </select>
                          </label>
                        </div>
                      )}
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Veteran or active-duty (self/spouse/parent)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="veteranOrActiveDutySelfSpouseParent" value={o} checked={form.veteranOrActiveDutySelfSpouseParent===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Special categories (pregnancy, Medicare, facility, hospice, HCBS/CHOICES, etc.) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Special Circumstances</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Pregnant or pregnant in last 12 months?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="pregnantOrLast12Months" value={o} checked={form.pregnantOrLast12Months===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.pregnantOrLast12Months === 'Yes' && (
                      <>
                        <label className="gts-field">
                          <span>Babies expected</span>
                          <input name="babiesExpected" value={form.babiesExpected} onChange={onChange} />
                        </label>
                        <fieldset className="gts-field">
                          <legend>Still pregnant?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label key={o} className="gts-radio">
                                <input type="radio" name="stillPregnant" value={o} checked={form.stillPregnant===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        {form.stillPregnant === 'Yes' ? (
                          <label className="gts-field">
                            <span>Due date</span>
                            <input type="date" name="dueDate" value={form.dueDate} onChange={onChange} />
                          </label>
                        ) : (
                          <label className="gts-field">
                            <span>Pregnancy end date</span>
                            <input type="date" name="pregnancyEndDate" value={form.pregnancyEndDate} onChange={onChange} />
                          </label>
                        )}
                      </>
                    )}

                    <fieldset className="gts-field">
                      <legend>Received medical services in last 3 months (under 19 or pregnant)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="retroactiveCoverage3mo" value={o} checked={form.retroactiveCoverage3mo===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.retroactiveCoverage3mo === 'Yes' && (
                      <label className="gts-field">
                        <span>Date of service</span>
                        <input type="date" name="retroactiveDateOfService" value={form.retroactiveDateOfService} onChange={onChange} />
                      </label>
                    )}

                    <fieldset className="gts-field">
                      <legend>Enrolled in/entitled to Medicare A or B?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="enrolledMedicareAB" value={o} checked={form.enrolledMedicareAB===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Emergency health problem & need help paying ER?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="emergencyHelpPayingER" value={o} checked={form.emergencyHelpPayingER===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Former foster care at 18 or older and on Medicaid?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="formerFosterCareAt18" value={o} checked={form.formerFosterCareAt18===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Under 65 and need/treating for breast or cervical cancer?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="breastCervicalTreatmentUnder65" value={o} checked={form.breastCervicalTreatmentUnder65===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>In a medical facility 30+ days (or expect to be)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="inMedicalFacility30Days" value={o} checked={form.inMedicalFacility30Days===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.inMedicalFacility30Days === 'Yes' && (
                      <>
                        <label className="gts-field">
                          <span>Facility name</span>
                          <input name="facilityName" value={form.facilityName} onChange={onChange} />
                        </label>
                        <label className="gts-field">
                          <span>Admit date</span>
                          <input type="date" name="facilityAdmitDate" value={form.facilityAdmitDate} onChange={onChange} />
                        </label>
                        <label className="gts-field">
                          <span>Doctor's name</span>
                          <input name="doctorName" value={form.doctorName} onChange={onChange} />
                        </label>
                        <label className="gts-field">
                          <span>Doctor's phone</span>
                          <input name="doctorPhone" value={form.doctorPhone} onChange={onChange} />
                        </label>
                      </>
                    )}

                    <fieldset className="gts-field">
                      <legend>Live in a medical facility or nursing home?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="livesInNursingHome" value={o} checked={form.livesInNursingHome===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {form.livesInNursingHome === 'Yes' && (
                      <label className="gts-field">
                        <span>Facility/Nursing home name</span>
                        <input name="nursingHomeName" value={form.nursingHomeName} onChange={onChange} />
                      </label>
                    )}

                    <fieldset className="gts-field">
                      <legend>Need hospice care?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="needsHospice" value={o} checked={form.needsHospice===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Qualifies for nursing home but wants care at home?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="qualifiesNursingHomePrefersHome" value={o} checked={form.qualifiesNursingHomePrefersHome===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Intellectual/developmental disability and want ICF/IID?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="icfIID" value={o} checked={form.icfIID===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Want HCBS / ECF CHOICES?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="needsECFChoicesHCBS" value={o} checked={form.needsECFChoicesHCBS===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Medicare & want help paying cost-sharing (MSP)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="wantsMedicareSavingsProgram" value={o} checked={form.wantsMedicareSavingsProgram===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Got SSI in the past (but not now)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="gotSSIInPast" value={o} checked={form.gotSSIInPast===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                      {form.gotSSIInPast === 'Yes' && (
                        <label className="gts-field">
                          <span>When did SSI end?</span>
                          <input type="date" name="ssiEndDate" value={form.ssiEndDate} onChange={onChange} />
                        </label>
                      )}
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Expenses to help you work because you are blind/disabled?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="blindDisabledWorkExpenses" value={o} checked={form.blindDisabledWorkExpenses===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Under 22 and working full time?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="under22WorkingFullTime" value={o} checked={form.under22WorkingFullTime===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Employment & Other Income (Person 1) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Person 1 — Work & Income</h5></div>
                  <div className="gts-panel">
                    <div className="gts-panel-title">Job 1</div>
                    <div className="gts-form-grid">
                      <label className="gts-field gts-span-2"><span>Employer</span><input name="job1Employer" value={form.job1Employer} onChange={onChange} /></label>
                      <label className="gts-field"><span>Employer Phone</span><input name="job1EmployerPhone" value={form.job1EmployerPhone} onChange={onChange} /></label>
                      <label className="gts-field"><span>Wages/Tips per pay ($)</span><input name="job1WagesPerPay" value={form.job1WagesPerPay} onChange={onChange} /></label>
                      <label className="gts-field"><span>Pay Frequency</span>
                        <select name="job1PayFrequency" value={form.job1PayFrequency} onChange={onChange}>
                          <option value="">Select…</option>
                          <option>Hourly</option><option>Daily</option><option>Weekly</option><option>Every 2 weeks</option>
                          <option>Twice a month</option><option>Monthly</option><option>Yearly</option><option>Irregularly</option>
                          <option>Semi-annually</option><option>One Time Only</option><option>Quarterly</option>
                        </select>
                      </label>
                      <label className="gts-field"><span>Avg hours per pay (if Hourly)</span><input name="job1AvgHoursPerPay" value={form.job1AvgHoursPerPay} onChange={onChange} /></label>
                    </div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Job 2</div>
                    <div className="gts-form-grid">
                      <label className="gts-field gts-span-2"><span>Employer</span><input name="job2Employer" value={form.job2Employer} onChange={onChange} /></label>
                      <label className="gts-field"><span>Employer Phone</span><input name="job2EmployerPhone" value={form.job2EmployerPhone} onChange={onChange} /></label>
                      <label className="gts-field"><span>Wages/Tips per pay ($)</span><input name="job2WagesPerPay" value={form.job2WagesPerPay} onChange={onChange} /></label>
                      <label className="gts-field"><span>Pay Frequency</span>
                        <select name="job2PayFrequency" value={form.job2PayFrequency} onChange={onChange}>
                          <option value="">Select…</option>
                          <option>Hourly</option><option>Daily</option><option>Weekly</option><option>Every 2 weeks</option>
                          <option>Twice a month</option><option>Monthly</option><option>Yearly</option><option>Irregularly</option>
                          <option>Semi-annually</option><option>One Time Only</option><option>Quarterly</option>
                        </select>
                      </label>
                      <label className="gts-field"><span>Avg hours per pay (if Hourly)</span><input name="job2AvgHoursPerPay" value={form.job2AvgHoursPerPay} onChange={onChange} /></label>
                    </div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Other Income (This Month)</div>
                    <div className="gts-form-grid">
                      <label className="gts-field gts-span-2 gts-checkboxline">
                        <input type="checkbox" name="oiNone" checked={form.oiNone} onChange={onChange} />
                        <span>No other income</span>
                      </label>

                      <label className="gts-field"><span>Social Security ($)</span><input name="oiSocialSecurity" value={form.oiSocialSecurity} onChange={onChange} /></label>
                      <label className="gts-field"><span>Unemployment ($)</span><input name="oiUnemployment" value={form.oiUnemployment} onChange={onChange} /></label>
                      <label className="gts-field"><span>Pension ($)</span><input name="oiPension" value={form.oiPension} onChange={onChange} /></label>
                      <label className="gts-field"><span>Retirement Accounts ($)</span><input name="oiRetirementAccounts" value={form.oiRetirementAccounts} onChange={onChange} /></label>
                      <label className="gts-field"><span>Rental/Royalties ($)</span><input name="oiRentalRoyalty" value={form.oiRentalRoyalty} onChange={onChange} /></label>
                      <label className="gts-field"><span>Alimony Received ($)</span><input name="oiAlimonyReceived" value={form.oiAlimonyReceived} onChange={onChange} /></label>
                      <label className="gts-field"><span>Net Farm/Fishing ($)</span><input name="oiNetFarmFish" value={form.oiNetFarmFish} onChange={onChange} /></label>
                      <label className="gts-field"><span>Census Worker ($)</span><input name="oiCensusWorker" value={form.oiCensusWorker} onChange={onChange} /></label>
                      <label className="gts-field"><span>Lottery ($)</span><input name="oiLottery" value={form.oiLottery} onChange={onChange} /></label>
                      <label className="gts-field">
                        <span>Other Income ($)</span>
                        <input name="oiOther" value={form.oiOther} onChange={onChange} />
                      </label>
                      <label className="gts-field">
                        <span>Other — Type</span>
                        <input name="oiOtherType" value={form.oiOtherType} onChange={onChange} />
                      </label>
                    </div>

                    {/* In-kind support (only appears if Social Security entered) */}
                    {form.oiSocialSecurity && (
                      <div className="gts-panel gts-panel-yellow gts-panel-margin-top">
                        <div className="gts-panel-title">In-Kind Support (if someone helps pay your food/housing)</div>
                        <div className="gts-form-grid">
                          <label className="gts-field">
                            <span>Does that person live with you?</span>
                            <select name="iksHelperLivesWithYou" value={form.iksHelperLivesWithYou} onChange={onChange}>
                              <option value="">Select…</option><option>Yes</option><option>No</option>
                            </select>
                          </label>
                          <label className="gts-field"><span>What do they help pay for?</span><input name="iksWhatTheyPayFor" value={form.iksWhatTheyPayFor} onChange={onChange} /></label>
                          <label className="gts-field"><span>Expense/Bill total ($)</span><input name="iksExpenseTotal" value={form.iksExpenseTotal} onChange={onChange} /></label>
                          <label className="gts-field"><span>You pay ($)</span><input name="iksYouPay" value={form.iksYouPay} onChange={onChange} /></label>
                          <label className="gts-field"><span>They pay ($)</span><input name="iksTheyPay" value={form.iksTheyPay} onChange={onChange} /></label>
                          <label className="gts-field"><span># People in home</span><input name="iksHouseholdCount" value={form.iksHouseholdCount} onChange={onChange} /></label>
                          <label className="gts-field">
                            <span>Does everyone in home get public assistance?</span>
                            <select name="iksAllPublicAssistance" value={form.iksAllPublicAssistance} onChange={onChange}>
                              <option value="">Select…</option><option>Yes</option><option>No</option>
                            </select>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="gts-panel gts-panel-margin-top">
                      <div className="gts-panel-title">Lump Sum & Self-Employment</div>
                      <div className="gts-form-grid">
                        <label className="gts-field">
                          <span>Received a lump sum in last 12 months?</span>
                          <select name="lumpSumInLast12mo" value={form.lumpSumInLast12mo} onChange={onChange}>
                            <option value="">Select…</option><option>Yes</option><option>No</option>
                          </select>
                        </label>
                        {form.lumpSumInLast12mo === 'Yes' && (
                          <>
                            <label className="gts-field"><span>Amount ($)</span><input name="lumpSumAmount" value={form.lumpSumAmount} onChange={onChange} /></label>
                            <label className="gts-field"><span>Date</span><input type="date" name="lumpSumDate" value={form.lumpSumDate} onChange={onChange} /></label>
                            <label className="gts-field"><span>Source</span><input name="lumpSumSource" value={form.lumpSumSource} onChange={onChange} /></label>
                          </>
                        )}

                        <label className="gts-field">
                          <span>Self-employed?</span>
                          <select name="selfEmployed" value={form.selfEmployed} onChange={onChange}>
                            <option value="">Select…</option><option>Yes</option><option>No</option>
                          </select>
                        </label>
                        {form.selfEmployed === 'Yes' && (
                          <>
                            <label className="gts-field"><span>What do you do?</span><input name="selfEmploymentType" value={form.selfEmploymentType} onChange={onChange} /></label>
                            <label className="gts-field"><span>Type of self-employment</span><input name="selfEmploymentKind" value={form.selfEmploymentKind} onChange={onChange} /></label>
                            <label className="gts-field"><span>Net monthly income ($)</span><input name="selfEmploymentNetMonthly" value={form.selfEmploymentNetMonthly} onChange={onChange} /></label>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Deductions */}
                    <div className="gts-panel gts-panel-margin-top">
                      <div className="gts-panel-title">Deductions</div>
                      <div className="gts-form-grid">
                        <label className="gts-field"><span>Medical Insurance ($/mo)</span><input name="dtMedicalIns" value={form.dtMedicalIns} onChange={onChange} /></label>
                        <label className="gts-field"><span>Dental Insurance ($/mo)</span><input name="dtDentalIns" value={form.dtDentalIns} onChange={onChange} /></label>
                        <label className="gts-field"><span>Vision Insurance ($/mo)</span><input name="dtVisionIns" value={form.dtVisionIns} onChange={onChange} /></label>
                        <label className="gts-field"><span>Flexible Spending (FSA) ($/mo)</span><input name="dtFSA" value={form.dtFSA} onChange={onChange} /></label>
                        <label className="gts-field"><span>Deferred Comp ($/mo)</span><input name="dtDeferredComp" value={form.dtDeferredComp} onChange={onChange} /></label>
                        <label className="gts-field"><span>Pre-Tax Life Premiums ($/mo)</span><input name="dtPreTaxLife" value={form.dtPreTaxLife} onChange={onChange} /></label>
                        <label className="gts-field"><span>Other Deduction ($/mo)</span><input name="dtOther" value={form.dtOther} onChange={onChange} /></label>
                        <label className="gts-field"><span>Other — Type</span><input name="dtOtherType" value={form.dtOtherType} onChange={onChange} /></label>

                        <label className="gts-field"><span>Alimony Paid ($/mo)</span><input name="tdAlimonyPaid" value={form.tdAlimonyPaid} onChange={onChange} /></label>
                        <label className="gts-field"><span>Alimony Order Date</span><input type="date" name="tdAlimonyOrderDate" value={form.tdAlimonyOrderDate} onChange={onChange} /></label>
                        <label className="gts-field"><span>HSA Deduction ($/mo)</span><input name="tdHSA" value={form.tdHSA} onChange={onChange} /></label>
                        <label className="gts-field"><span>Student Loan Interest ($/mo)</span><input name="tdStudentLoanInterest" value={form.tdStudentLoanInterest} onChange={onChange} /></label>
                        <label className="gts-field"><span>Tuition & Fees ($/mo)</span><input name="tdTuitionFees" value={form.tdTuitionFees} onChange={onChange} /></label>
                        <label className="gts-field"><span>Educator Expenses ($/mo)</span><input name="tdEducatorExpenses" value={form.tdEducatorExpenses} onChange={onChange} /></label>
                        <label className="gts-field"><span>Business Expenses ($/mo)</span><input name="tdBusinessExpenses" value={form.tdBusinessExpenses} onChange={onChange} /></label>
                        <label className="gts-field"><span>Deductible Self-Employment ($/mo)</span><input name="tdSelfEmploymentDeductible" value={form.tdSelfEmploymentDeductible} onChange={onChange} /></label>
                        <label className="gts-field"><span>Military Moving Expense (total)</span><input name="tdMilitaryMoveExpenseTotal" value={form.tdMilitaryMoveExpenseTotal} onChange={onChange} /></label>
                        <label className="gts-field"><span>Other Tax Deduction ($/mo)</span><input name="tdOther" value={form.tdOther} onChange={onChange} /></label>
                        <label className="gts-field"><span>Other Tax Deduction — Type</span><input name="tdOtherType" value={form.tdOtherType} onChange={onChange} /></label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Household Members (add more people) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head">
                    <h5>Other People in Your Household</h5>
                    <div className="gts-small-note">Add each person who lives with you (spouse, children under 21, others on your tax return).</div>
                  </div>
                  <div className="gts-panel">
                    {household.map((p, idx) => (
                      <div key={idx} className="gts-panel gts-panel-stack" style={{ marginBottom: 10 }}>
                        <div className="gts-panel-title">Person {idx + 2}</div>
                        <div className="gts-form-grid">
                          <label className="gts-field gts-span-2"><span>Full Name</span><input value={p.fullName||''} onChange={(e)=>onChangeHH(idx,'fullName',e.target.value)} /></label>
                          <label className="gts-field"><span>DOB</span><input type="date" value={p.dob||''} onChange={(e)=>onChangeHH(idx,'dob',e.target.value)} /></label>
                          <label className="gts-field"><span>Sex</span>
                            <select value={p.sex||''} onChange={(e)=>onChangeHH(idx,'sex',e.target.value)}>
                              <option value="">Select…</option><option>Male</option><option>Female</option>
                            </select>
                          </label>
                          <label className="gts-field"><span>Relationship to Person 1</span><input value={p.relationshipToP1||''} onChange={(e)=>onChangeHH(idx,'relationshipToP1',e.target.value)} /></label>
                          <label className="gts-field"><span>SSN (optional)</span><input value={p.ssn||''} onChange={(e)=>onChangeHH(idx,'ssn',e.target.value)} /></label>
                          <label className="gts-field"><span>Apply for this person?</span>
                            <select value={p.applying||'Yes'} onChange={(e)=>onChangeHH(idx,'applying',e.target.value)}>
                              <option>Yes</option><option>No</option>
                            </select>
                          </label>

                          <label className="gts-field"><span>Resident of GTS?</span>
                            <select value={p.residentOfState||''} onChange={(e)=>onChangeHH(idx,'residentOfState',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Temp out of state?</span>
                            <select value={p.tempOutOfState||''} onChange={(e)=>onChangeHH(idx,'tempOutOfState',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Plan to return?</span>
                            <select value={p.planReturnToState||''} onChange={(e)=>onChangeHH(idx,'planReturnToState',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Return date</span><input type="date" value={p.planReturnDate||''} onChange={(e)=>onChangeHH(idx,'planReturnDate',e.target.value)} /></label>

                          <label className="gts-field"><span>U.S. Citizen?</span>
                            <select value={p.usCitizen||''} onChange={(e)=>onChangeHH(idx,'usCitizen',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Naturalized/Derived?</span>
                            <select value={p.naturalizedOrDerived||''} onChange={(e)=>onChangeHH(idx,'naturalizedOrDerived',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Eligible Immigration?</span>
                            <select value={p.eligibleImmigration||''} onChange={(e)=>onChangeHH(idx,'eligibleImmigration',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>

                          <label className="gts-field"><span>Pregnant (or in last 12 months)?</span>
                            <select value={p.pregnantOrLast12Months||''} onChange={(e)=>onChangeHH(idx,'pregnantOrLast12Months',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>
                          <label className="gts-field"><span>Babies expected</span><input value={p.babiesExpected||''} onChange={(e)=>onChangeHH(idx,'babiesExpected',e.target.value)} /></label>

                          <label className="gts-field"><span>Medicare A/B?</span>
                            <select value={p.medicareAB||''} onChange={(e)=>onChangeHH(idx,'medicareAB',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                          </label>

                          <label className="gts-field">
                            <span>Preferred Health Plan</span>
                            <select value={p.mcoChoice||form.mcoChoice} onChange={(e)=>onChangeHH(idx,'mcoChoice',e.target.value)}>
                              <option>Wellpoint</option><option>BlueCare GTS</option><option>UnitedHealthcare Community Plan</option>
                            </select>
                          </label>
                        </div>

                        <div className="gts-actions">
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removePerson(idx)}>Remove Person</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={addPerson}>Add Another Person</button>
                    </div>
                  </div>
                </div>

                {/* Current Health Coverage */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Current Health Coverage</h5></div>
                  <div className="gts-form-grid">
                    <fieldset className="gts-field">
                      <legend>Is anyone enrolled in other health insurance (not GTSCare/CoverKids)?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="hasOtherInsurance" value={o} checked={form.hasOtherInsurance===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.hasOtherInsurance === 'Yes' && (
                      <>
                        <label className="gts-field gts-span-2"><span>Insurance Company</span><input name="otherInsCompany" value={form.otherInsCompany} onChange={onChange} /></label>
                        <label className="gts-field">
                          <span>Coverage Type</span>
                          <select name="otherInsType" value={form.otherInsType} onChange={onChange}>
                            <option value="">Select…</option>
                            <option>Medicare</option><option>TRICARE</option><option>Employer</option><option>COBRA</option>
                            <option>Retiree health plan</option><option>VA Health Care Programs</option><option>Peace Corps</option>
                            <option>Limited benefit plan</option>
                          </select>
                        </label>
                        <label className="gts-field"><span>Employer (if employer plan)</span><input name="otherInsEmployerName" value={form.otherInsEmployerName} onChange={onChange} /></label>
                        <label className="gts-field gts-span-2"><span>Who is covered (names)</span><input name="otherInsWhoIsCovered" value={form.otherInsWhoIsCovered} onChange={onChange} /></label>
                        <label className="gts-field"><span>Policy #</span><input name="otherInsPolicyNumber" value={form.otherInsPolicyNumber} onChange={onChange} /></label>
                        <label className="gts-field"><span>Group #</span><input name="otherInsGroupNumber" value={form.otherInsGroupNumber} onChange={onChange} /></label>
                        <label className="gts-field"><span>Coverage start date</span><input type="date" name="otherInsStartDate" value={form.otherInsStartDate} onChange={onChange} /></label>
                        <fieldset className="gts-field">
                          <legend>State employee benefit plan?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label key={o} className="gts-radio">
                                <input type="radio" name="otherInsStateEmployeePlan" value={o} checked={form.otherInsStateEmployeePlan===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <fieldset className="gts-field">
                          <legend>Limited benefit plan?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label key={o} className="gts-radio">
                                <input type="radio" name="otherInsLimitedPlan" value={o} checked={form.otherInsLimitedPlan===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <fieldset className="gts-field">
                          <legend>Plan covers maternity?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label key={o} className="gts-radio">
                                <input type="radio" name="otherInsMaternityCovered" value={o} checked={form.otherInsMaternityCovered===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        <fieldset className="gts-field gts-span-2">
                          <legend>Lost Medicare because returned to work?</legend>
                          <div className="gts-radio-list">
                            {['Yes','No'].map(o => (
                              <label key={o} className="gts-radio">
                                <input type="radio" name="lostMedicareDueToWork" value={o} checked={form.lostMedicareDueToWork===o} onChange={onChange} />
                                <span>{o}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                        {form.lostMedicareDueToWork === 'Yes' && (
                          <label className="gts-field gts-span-2"><span>Who?</span><input name="lostMedicareWho" value={form.lostMedicareWho} onChange={onChange} /></label>
                        )}
                      </>
                    )}

                    <fieldset className="gts-field gts-span-2">
                      <legend>Access to other coverage but not enrolled?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="accessToOtherCoverageNotEnrolled" value={o} checked={form.accessToOtherCoverageNotEnrolled===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Family Resources (optional, but included for completeness) */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Family Resources (Optional for some groups)</h5></div>
                  <div className="gts-panel">
                    <div className="gts-panel-title">Financial Resources</div>
                    {resourceRows.map((row, idx) => (
                      <div className="gts-form-grid" key={idx} style={{ marginBottom: 8 }}>
                        <label className="gts-field"><span>Type</span><input value={row.type||''} onChange={(e)=>updateRow(resourceRows,setResourceRows,idx,'type',e.target.value)} placeholder="Cash, Checking, Savings, IRA, Stocks, Trust…" /></label>
                        <label className="gts-field"><span>Value ($)</span><input value={row.value||''} onChange={(e)=>updateRow(resourceRows,setResourceRows,idx,'value',e.target.value)} /></label>
                        <label className="gts-field"><span>Owners (names)</span><input value={row.owners||''} onChange={(e)=>updateRow(resourceRows,setResourceRows,idx,'owners',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(resourceRows,setResourceRows,idx,{type:'',value:'',owners:''})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions">
                      <button type="button" className="btn btn-secondary" onClick={()=>addRow(resourceRows,setResourceRows,{type:'',value:'',owners:''})}>Add Resource</button>
                    </div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Property</div>
                    {propertyRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field gts-span-2"><span>Owners</span><input value={row.owners||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'owners',e.target.value)} /></label>
                        <label className="gts-field"><span>Use</span><input value={row.use||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'use',e.target.value)} placeholder="House, land, rental, farm…" /></label>
                        <label className="gts-field"><span>Anyone live there?</span>
                          <select value={row.anyoneLiveThere||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'anyoneLiveThere',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                        </label>
                        <label className="gts-field"><span>Who lives there</span><input value={row.whoLivesThere||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'whoLivesThere',e.target.value)} /></label>
                        <label className="gts-field"><span>Intend to return?</span>
                          <select value={row.intendReturn||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'intendReturn',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                        </label>
                        <label className="gts-field"><span>Spouse/child live there?</span>
                          <select value={row.spouseOrChildLivesThere||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'spouseOrChildLivesThere',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                        </label>
                        <label className="gts-field"><span>Receives rent?</span>
                          <select value={row.receivesRent||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'receivesRent',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                        </label>
                        <label className="gts-field"><span>Monthly rent income ($)</span><input value={row.monthlyRentIncome||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'monthlyRentIncome',e.target.value)} /></label>
                        <label className="gts-field"><span>Amount owed ($)</span><input value={row.amountOwed||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'amountOwed',e.target.value)} /></label>
                        <label className="gts-field"><span>Value ($)</span><input value={row.value||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'value',e.target.value)} /></label>

                        <label className="gts-field gts-span-2"><span>Property Address</span><input value={row.address||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'address',e.target.value)} /></label>
                        <label className="gts-field"><span>City</span><input value={row.city||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'city',e.target.value)} /></label>
                        <label className="gts-field"><span>State</span><input value={row.state||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'state',e.target.value)} maxLength={2} /></label>
                        <label className="gts-field"><span>ZIP</span><input value={row.zip||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'zip',e.target.value)} maxLength={5} /></label>
                        <label className="gts-field"><span>County</span><input value={row.county||''} onChange={(e)=>updateRow(propertyRows,setPropertyRows,idx,'county',e.target.value)} /></label>

                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(propertyRows,setPropertyRows,idx,{})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions"><button type="button" className="btn btn-secondary" onClick={()=>addRow(propertyRows,setPropertyRows,{})}>Add Property</button></div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Life Insurance</div>
                    {lifeInsRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field"><span>Owners</span><input value={row.owners||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'owners',e.target.value)} /></label>
                        <label className="gts-field"><span>Policy Type</span>
                          <select value={row.policyType||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'policyType',e.target.value)}>
                            <option value="">Select…</option><option>Term/Group</option><option>Whole/Universal</option>
                          </select>
                        </label>
                        <label className="gts-field"><span>Face Value ($)</span><input value={row.faceValue||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'faceValue',e.target.value)} /></label>
                        <label className="gts-field"><span>Cash Surrender ($)</span><input value={row.cashSurrender||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'cashSurrender',e.target.value)} /></label>
                        <label className="gts-field"><span>Policy #</span><input value={row.policyNumber||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'policyNumber',e.target.value)} /></label>
                        <label className="gts-field gts-span-2"><span>Company</span><input value={row.company||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'company',e.target.value)} /></label>
                        <label className="gts-field"><span>Address</span><input value={row.companyAddress||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'companyAddress',e.target.value)} /></label>
                        <label className="gts-field"><span>City</span><input value={row.companyCity||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'companyCity',e.target.value)} /></label>
                        <label className="gts-field"><span>State</span><input value={row.companyState||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'companyState',e.target.value)} maxLength={2} /></label>
                        <label className="gts-field"><span>ZIP</span><input value={row.companyZip||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'companyZip',e.target.value)} maxLength={5} /></label>
                        <label className="gts-field"><span>Company Phone</span><input value={row.companyPhone||''} onChange={(e)=>updateRow(lifeInsRows,setLifeInsRows,idx,'companyPhone',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(lifeInsRows,setLifeInsRows,idx,{})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions"><button type="button" className="btn btn-secondary" onClick={()=>addRow(lifeInsRows,setLifeInsRows,{})}>Add Life Insurance</button></div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Burial Resources</div>
                    {burialRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field"><span>Value ($)</span><input value={row.value||''} onChange={(e)=>updateRow(burialRows,setBurialRows,idx,'value',e.target.value)} /></label>
                        <label className="gts-field"><span>Amount Owed ($)</span><input value={row.amountOwed||''} onChange={(e)=>updateRow(burialRows,setBurialRows,idx,'amountOwed',e.target.value)} /></label>
                        <label className="gts-field"><span>Type</span><input value={row.type||''} onChange={(e)=>updateRow(burialRows,setBurialRows,idx,'type',e.target.value)} /></label>
                        <label className="gts-field"><span>Designated For</span><input value={row.designatedFor||''} onChange={(e)=>updateRow(burialRows,setBurialRows,idx,'designatedFor',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(burialRows,setBurialRows,idx,{})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions"><button type="button" className="btn btn-secondary" onClick={()=>addRow(burialRows,setBurialRows,{})}>Add Burial Resource</button></div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Vehicles</div>
                    {vehicleRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field"><span>Owners</span><input value={row.owners||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'owners',e.target.value)} /></label>
                        <label className="gts-field"><span>Year</span><input value={row.year||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'year',e.target.value)} /></label>
                        <label className="gts-field"><span>Make</span><input value={row.make||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'make',e.target.value)} /></label>
                        <label className="gts-field"><span>Model</span><input value={row.model||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'model',e.target.value)} /></label>
                        <label className="gts-field"><span>Amount Owed ($)</span><input value={row.amountOwed||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'amountOwed',e.target.value)} /></label>
                        <label className="gts-field"><span>Value ($)</span><input value={row.value||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'value',e.target.value)} /></label>
                        <label className="gts-field"><span>Income from use?</span>
                          <select value={row.incomeFromUse||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'incomeFromUse',e.target.value)}><option value="">Select…</option><option>Yes</option><option>No</option></select>
                        </label>
                        <label className="gts-field"><span>Use</span>
                          <select value={row.use||''} onChange={(e)=>updateRow(vehicleRows,setVehicleRows,idx,'use',e.target.value)}>
                            <option value="">Select…</option>
                            <option>Household Transportation</option><option>Self-Employment</option><option>Income Producing</option>
                            <option>Recreational</option><option>Tools of the Trade</option><option>Homestead</option>
                          </select>
                        </label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(vehicleRows,setVehicleRows,idx,{})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions"><button type="button" className="btn btn-secondary" onClick={()=>addRow(vehicleRows,setVehicleRows,{})}>Add Vehicle</button></div>
                  </div>

                  <div className="gts-panel gts-panel-margin-top">
                    <div className="gts-panel-title">Sold / Traded / Given Away (last 5 years)</div>
                    {soldGiftedRows.map((row, idx) => (
                      <div key={idx} className="gts-form-grid" style={{ marginBottom: 8 }}>
                        <label className="gts-field"><span>Resource Type</span><input value={row.resourceType||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'resourceType',e.target.value)} /></label>
                        <label className="gts-field"><span>Who owned it</span><input value={row.whoOwned||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'whoOwned',e.target.value)} /></label>
                        <label className="gts-field"><span>To whom</span><input value={row.toWhom||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'toWhom',e.target.value)} /></label>
                        <label className="gts-field"><span>Why</span><input value={row.why||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'why',e.target.value)} /></label>
                        <label className="gts-field"><span>Date</span><input type="date" value={row.date||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'date',e.target.value)} /></label>
                        <label className="gts-field"><span>Value at time ($)</span><input value={row.valueAtTime||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'valueAtTime',e.target.value)} /></label>
                        <label className="gts-field"><span>Money received ($)</span><input value={row.moneyReceived||''} onChange={(e)=>updateRow(soldGiftedRows,setSoldGiftedRows,idx,'moneyReceived',e.target.value)} /></label>
                        <div className="gts-field" style={{ justifyContent: 'flex-end' }}>
                          <button type="button" className="btn btn-outline-primary" onClick={()=>removeRow(soldGiftedRows,setSoldGiftedRows,idx,{})}>Remove</button>
                        </div>
                      </div>
                    ))}
                    <div className="gts-actions"><button type="button" className="btn btn-secondary" onClick={()=>addRow(soldGiftedRows,setSoldGiftedRows,{})}>Add Item</button></div>
                  </div>

                  <div className="gts-form-grid" style={{ marginTop: 10 }}>
                    <fieldset className="gts-field">
                      <legend>Household has shelter/utility/child support expenses?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="hasShelterOrUtilityOrChildSupport" value={o} checked={form.hasShelterOrUtilityOrChildSupport===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <fieldset className="gts-field">
                      <legend>Medical or dental bills in last 3 months?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label className="gts-radio" key={o}>
                            <input type="radio" name="hasMedicalDentalLast3mo" value={o} checked={form.hasMedicalDentalLast3mo===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.hasMedicalDentalLast3mo === 'Yes' && (
                      <>
                        <label className="gts-field"><span>Amount ($)</span><input name="medBillAmount" value={form.medBillAmount} onChange={onChange} /></label>
                        <label className="gts-field"><span>Date of service</span><input type="date" name="medBillDateOfService" value={form.medBillDateOfService} onChange={onChange} /></label>
                        <label className="gts-field gts-span-2"><span>Pay to (provider)</span><input name="medBillPayTo" value={form.medBillPayTo} onChange={onChange} /></label>
                      </>
                    )}
                  </div>
                </div>

                {/* Renewal Permissions */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Renewal &amp; Verification Permissions</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field">
                      <span>Permission to try to renew using tax data</span>
                      <select name="permitTaxDataYears" value={form.permitTaxDataYears} onChange={onChange}>
                        <option value="5">5 Years</option>
                        <option value="4">4 Years</option>
                        <option value="3">3 Years</option>
                        <option value="2">2 Years</option>
                        <option value="1">1 Year</option>
                        <option value="0">Don’t use tax data</option>
                      </select>
                    </label>

                    <fieldset className="gts-field">
                      <legend>OK to verify household resources with a credit reporting agency?</legend>
                      <div className="gts-radio-list">
                        {['Yes','No'].map(o => (
                          <label key={o} className="gts-radio">
                            <input type="radio" name="permitCreditAgencyCheck" value={o} checked={form.permitCreditAgencyCheck===o} onChange={onChange} />
                            <span>{o}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                </div>

                {/* Declaration & Signature */}
                <div className="gts-form-section">
                  <div className="gts-form-section-head"><h5>Declaration &amp; Signature</h5></div>
                  <div className="gts-form-grid">
                    <label className="gts-field gts-span-2 gts-checkboxline">
                      <input type="checkbox" name="certify" checked={form.certify} onChange={onChange} />
                      <span>
                        I certify the facts are true and understand I must report changes, and that program rules (including third-party liability and estate
                        recovery, where applicable) apply. <span className="gts-required">*</span>
                      </span>
                      {errors.certify && <em className="gts-error">{errors.certify}</em>}
                    </label>

                    <label className="gts-field">
                      <span>Signature (type full name) <span className="gts-required">*</span></span>
                      <input name="signature" value={form.signature} onChange={onChange} />
                      {errors.signature && <em className="gts-error">{errors.signature}</em>}
                    </label>
                    <label className="gts-field">
                      <span>Date <span className="gts-required">*</span></span>
                      <input type="date" name="signedDate" value={form.signedDate} onChange={onChange} />
                      {errors.signedDate && <em className="gts-error">{errors.signedDate}</em>}
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="gts-actions gts-actions-form">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                  <a 
                    href="/guides/GTSCarePaperApp.pdf" 
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download Application (PDF)
                  </a>
                  <button type="button" className="btn btn-outline-primary" onClick={resetForm} disabled={submitting}>
                    Reset Form
                  </button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12 }}>
              <Link to="/forms" className="gts-muted-link">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: 5 }} />
                Back to Forms &amp; Applications
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MedicaidChipApplication;
